import { ChevronRight } from "lucide-react";
import { Link } from "react-router";

interface TenantDefaults {
  name?: string | null;
  defaultPreviewImageUrl?: string | null;
  defaultHeaderHtml?: string | null;
  defaultFooterHtml?: string | null;
  defaultCountersignerName?: string | null;
  defaultMarginTop?: number | null;
  defaultMarginBottom?: number | null;
  defaultMarginLeft?: number | null;
  defaultMarginRight?: number | null;
  defaultPreviewGeneratedAt?: string | null;
}

interface DefaultsStripProps {
  tenant?: TenantDefaults | null;
  loading?: boolean;
}

function summarize(tenant: TenantDefaults | null | undefined): string {
  if (!tenant) return "Header · Footer · Margins · Countersigner";
  const parts: string[] = [];
  if (tenant.defaultHeaderHtml) parts.push("Header");
  if (tenant.defaultFooterHtml) parts.push("Footer");
  if (tenant.defaultCountersignerName) parts.push("Countersigner");
  const top = tenant.defaultMarginTop ?? 0.75;
  const left = tenant.defaultMarginLeft ?? 0.5;
  parts.push(`${top.toFixed(2)}/${left.toFixed(2)} margins`);
  return parts.join(" · ");
}

function relativeTime(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  if (Number.isNaN(t)) return null;
  const diffMs = Date.now() - t;
  if (diffMs < 60_000) return "just now";
  const minutes = Math.round(diffMs / 60_000);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.round(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.round(hours / 24);
  return `${days}d ago`;
}

function PlaceholderThumb() {
  return (
    <div className="w-[78px] flex-shrink-0">
      <div
        className="bg-paper-hi rounded-sm relative"
        style={{
          aspectRatio: "8.5 / 11",
          boxShadow:
            "0 0 0 1px rgba(11,37,69,0.08), 0 12px 24px -8px rgba(11,37,69,0.10)",
        }}
        aria-hidden="true"
      >
        <div className="absolute top-[6%] left-[8%] right-[8%] hair-b border-b pb-1 flex items-center gap-1">
          <div className="w-2.5 h-2.5 rounded-sm bg-ink-2"></div>
          <div className="text-[5px] font-medium">Tenant</div>
        </div>
        <div className="absolute top-[20%] left-[8%] right-[8%] space-y-0.5">
          <div className="h-[3px] bg-ink-6/40 rounded w-[96%]"></div>
          <div className="h-[3px] bg-ink-6/40 rounded w-[96%]"></div>
          <div className="h-[3px] bg-ink-6/40 rounded w-[78%]"></div>
          <div className="h-[3px] bg-ink-6/40 rounded w-[96%]"></div>
          <div className="h-[3px] bg-ink-6/40 rounded w-[52%]"></div>
        </div>
        <div className="absolute bottom-[6%] left-[8%] right-[8%] hair-t border-t pt-0.5 text-[4px] text-ink-4 flex justify-between">
          <span>Confidential</span>
          <span>Pg 1</span>
        </div>
      </div>
    </div>
  );
}

export function DefaultsStrip({ tenant, loading }: DefaultsStripProps) {
  const summary = summarize(tenant);
  const lastEdited = relativeTime(tenant?.defaultPreviewGeneratedAt);
  const tenantLabel = tenant?.name ?? "Defaults";

  return (
    <Link
      to="/settings/agreements/defaults"
      className="block bg-paper-hi rounded-lg shadow-card hover:shadow-lift transition-shadow p-4 mb-5 group"
    >
      <div className="flex items-center gap-5">
        {tenant?.defaultPreviewImageUrl ? (
          <img
            src={tenant.defaultPreviewImageUrl}
            alt=""
            width={78}
            height={101}
            loading="lazy"
            className="w-[78px] h-auto rounded-sm flex-shrink-0"
            style={{
              boxShadow:
                "0 0 0 1px rgba(11,37,69,0.08), 0 12px 24px -8px rgba(11,37,69,0.10)",
            }}
          />
        ) : (
          <PlaceholderThumb />
        )}

        <div className="flex-1 min-w-0">
          <div className="text-[11px] text-tide font-medium uppercase tracking-wider mb-1">
            Defaults
          </div>
          <div className="text-[14.5px] font-medium truncate">
            {loading ? "Loading…" : `${tenantLabel} · Agreement design`}
          </div>
          <div className="text-[12px] text-ink-4 mt-0.5 truncate">
            {summary}
            {lastEdited && (
              <>
                {" "}
                · last edited <span className="num">{lastEdited}</span>
              </>
            )}
          </div>
        </div>

        <div className="text-tide flex items-center gap-1 text-[12.5px] font-medium group-hover:text-tide-d">
          Edit defaults
          <ChevronRight size={13} strokeWidth={2} />
        </div>
      </div>
    </Link>
  );
}
